import React from 'react';
import { Trophy } from 'lucide-react';
import { PlayerWeeklyCardProps } from '../../types';
import Sparkline from './Sparkline';

/**
 * PlayerWeeklyCard - Comprehensive player card for weekly roster view
 * Features:
 * - Name, team, position in top row
 * - Draft badge (R<x> • P<y>) or acquisition badge
 * - Week points, start/bench status, opponent matchup in second row
 * - Transaction markers strip at bottom
 * - Sparkline with start/bench indicators on right edge
 */
const PlayerWeeklyCard: React.FC<PlayerWeeklyCardProps> = ({
  player,
  selectedWeek,
  onPlayerClick
}) => {
  const getPositionColor = (position: string) => {
    const colors: { [key: string]: string } = {
      'QB': 'text-position-qb',
      'RB': 'text-position-rb',
      'WR': 'text-position-wr',
      'TE': 'text-position-te',
      'K': 'text-position-kdst',
      'D/ST': 'text-position-kdst',
      'DEF': 'text-position-kdst'
    };
    return colors[position] || 'text-text-secondary';
  };



  return (
    <div 
      className="relative bg-background-secondary border border-fieldline p-4 rounded-lg hover:bg-background-hover transition-colors cursor-pointer flex flex-col h-[200px] w-full"
      onClick={() => onPlayerClick?.(player.player_id)}
    >
      {/* Top Row: Name, Team, Position, Status */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 mr-3">
          <h4 className="text-text-primary font-semibold text-sm leading-tight mb-1">
            {player.name}
          </h4>
          <div className="flex items-center space-x-2 text-xs">
            <span className="text-text-secondary">{player.nfl_team}</span>
            <span className={`font-semibold ${getPositionColor(player.position)}`}>
              {player.position}
            </span>
          </div>
        </div>
        
        <div className="flex flex-col items-end space-y-1 flex-shrink-0">
          {/* Draft Badge */}
          {player.draft_info ? (
            <div className="px-2 py-1 bg-accent-info/15 text-accent-info text-xs rounded font-mono font-medium flex items-center space-x-1">
              <Trophy className="w-3 h-3" />
              <span>R{player.draft_info.round}•P{player.draft_info.overall_pick}</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Points and Status side by side */}
      <div className="flex justify-center items-center space-x-6 mb-auto">
        {/* Week Points */}
        <div className="text-center">
          <div className="text-text-primary font-bold text-xl">
            {player.week_points.toFixed(1)}
          </div>
          <div className="text-text-secondary text-xs">
            vs {player.projected_points.toFixed(1)}
          </div>
        </div>
        
        {/* Start/Bench Status with Opponent below */}
        <div className="text-center">
          <div className={`px-2 py-1 rounded text-xs font-bold mb-1 ${
            player.is_starter 
              ? 'bg-accent-primary/15 text-accent-primary border border-accent-primary/30'
              : 'bg-background-primary/30 text-text-secondary border border-fieldline'
          }`}>
            {player.is_starter ? 'START' : 'BENCH'}
          </div>
          
          {player.opponent_matchup && (
            <div className="text-text-secondary text-xs font-medium">
              {player.opponent_matchup}
            </div>
          )}
        </div>
      </div>

      {/* Sparkline at bottom of card */}
      <div className="mt-auto pt-3 pb-2 w-full overflow-hidden">
        <div className="w-full h-6">
          <Sparkline
            data={player.weekly_performance}
            height={24}
            color="#3b82f6"
            showDots={true}
          />
        </div>
      </div>

      {/* Lineup Slot Indicator (small indicator on card edge) */}
      <div className="absolute top-2 left-0 w-1 h-8 rounded-r-md opacity-60"
        style={{
          backgroundColor: player.is_starter ? '#10b981' : '#6b7280'
        }}
        title={`Lineup Slot: ${player.lineup_slot}`}
      />
    </div>
  );
};

export default PlayerWeeklyCard;
